/**
 * A catalogue id a project's publish registered and never put a page up under, kept per project
 * in this browser until a publish succeeds.
 *
 * F21 (web deep-dive, 2026-09-30): a build whose host call failed after the register dropped the
 * id, and the later "publish it from Settings" registered a second catalogue row — which found
 * the slug taken by the first and put the page at `<slug>-2`, with the first left behind.
 *
 * NOT on the definition. A record with `hostedAppId` reads as an app that has been online (the
 * AppPublished count, the Settings actions), and this one never was; the build intent that knew
 * the id is cleared the moment the app is saved. So a small record of its own, beside them.
 *
 * Per browser, and best effort like every storage this client touches: lost, the next publish
 * registers a new row, which is where it was.
 */
const KEY = 'saddle.unhostedApps';

/**
 * The catalogue id a failed `publishToSaddle` registered and never put a page up under, if it
 * did — read off the error it threw, to be handed back as `registeredAppId` next time.
 *
 * Read here rather than exported from api/hosting, which many screen tests replace with a mock
 * that has `publishToSaddle` alone: a reader living there broke every one of them.
 */
export function registeredAppIdOf(err: unknown): string | undefined {
  const id = (err as { registeredAppId?: unknown } | null)?.registeredAppId;
  return typeof id === 'string' && id ? id : undefined;
}

function readAll(): Record<string, string> {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, string>) : {};
  } catch {
    return {};
  }
}

function writeAll(all: Record<string, string>): void {
  try {
    if (Object.keys(all).length) localStorage.setItem(KEY, JSON.stringify(all));
    else localStorage.removeItem(KEY);
  } catch {
    /* a lost record costs a second catalogue row, never the publish */
  }
}

/** The id to hand `publishToSaddle` as `registeredAppId`, if an earlier publish left one. */
export function unhostedAppFor(projectId: string): string | undefined {
  const id = readAll()[projectId];
  return typeof id === 'string' && id ? id : undefined;
}

export function keepUnhostedApp(projectId: string, appId: string): void {
  writeAll({ ...readAll(), [projectId]: appId });
}

/** The page went up (or the project is gone): the id is on the record now, or nowhere. */
export function forgetUnhostedApp(projectId: string): void {
  const all = readAll();
  if (!(projectId in all)) return;
  delete all[projectId];
  writeAll(all);
}
